import { useState } from 'react';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useDosimetryFieldsConfig from './useDosimetryFieldsConfig';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../../hooks/useTranslate';

export interface IDosimetryForm {
    id: string;
    cuit: string;
    dosimetryType: string;
    dosimetryMethod: string;
    personalQuantity: string;
}

export const initialDosimetryForm: IDosimetryForm = {
    id: '',
    cuit: '',
    dosimetryType: '',
    dosimetryMethod: '',
    personalQuantity: ''
};

export interface DosimetryFormProps {
    selectedCarcinogenicReportId: number | null;
    selectedRadiologicalEquipment?: IDosimetryForm;
    isEditing: boolean;
    handleCloseDrawer: any;
}

interface GetInitialData {
    selectedRadiologicalEquipment: any;
    isEditing: boolean;
}

const getInitialData = ({ selectedRadiologicalEquipment, isEditing }: GetInitialData) => {
    if (!isEditing) return initialDosimetryForm;
    return {
        id: selectedRadiologicalEquipment?.id || '',
        cuit: selectedRadiologicalEquipment?.cuitDisometria || '',
        dosimetryType: selectedRadiologicalEquipment?.tipoDisometria || '',
        dosimetryMethod: selectedRadiologicalEquipment?.metodoDisometria || '',
        personalQuantity: selectedRadiologicalEquipment?.cantidadPersonalDisometria || ''
    };
};

export const useDosimetryForm = ({
    selectedCarcinogenicReportId,
    selectedRadiologicalEquipment,
    isEditing,
    handleCloseDrawer
}: DosimetryFormProps) => {
    const [formData, setFormData] = useState<IDosimetryForm>(
        getInitialData({ selectedRadiologicalEquipment, isEditing })
    );
    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.radiologicalEquipment'
    });

    const {translate} = useTranslate(t);

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { dosimetryFieldsConfig } = useDosimetryFieldsConfig({
        t,
        formData,
        handleBlurValidation
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = ({ onValidationSuccess }: any) => {
        if (!validateForm(formData, dosimetryFieldsConfig)) return;
        onValidationSuccess();
    };

    const resetForm = () => {
        setFormData(initialDosimetryForm);
    };

    return {
        formData,
        errors,
        dosimetryFieldsConfig,
        translate,
        resetForm,
        handleChange,
        validateForm,
        handleSubmit
    };
};
